import { gridSize, gridSizeMdLg12, useLoaders, useMapComponent } from "@/shared";
import { CustomAutocomplete } from "@/shared/components/ui";
import { DoNotDisturb, Visibility } from "@mui/icons-material";
import { Alert, Box, Divider, Fade, Grid, Paper, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { createTheme, alpha } from "@mui/material/styles";
import { Contrato } from "@/shared/interfaces";
import { ScrollableDialogProps } from "@/shared/components/ui/Dialogs";
import { postPoligono, useFetchContratos } from "@/shared/actions/erp";
import { IVision360Cliente } from "@/shared/interfaces/erp/tickets-soporte-clientes";
import { toast } from "react-toastify";

import VisitaForm from "../OrdenServicioVisita/VisitaForm";

// Tema con colores cálidos
export const warmTheme = createTheme({
  palette: {
    primary: {
      main: "#355872", // Naranja cálido
      light: "#7AAACE",
      dark: "#9CD5FF",
    },
    secondary: {
      main: "#F7F8F0", // Beige cálido
      light: "#F6E7BC",
      dark: "#F0F0DB",
    },
    background: {
      default: "#FFF8F0",
      paper: "#FFFBF5",
    },
    error: {
      main: "#D32F2F",
    },
    success: {
      main: "#4CAF50",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          padding: "10px 24px",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            "&:hover fieldset": {
              borderColor: "#E07B39",
            },
          },
        },
      },
    },
  },
});

export type ServiceTypeOrdenServicio =
  | "TRASLADO"
  | "VISITA"
  | "NO_REQUIERE"
  | null;

type OrdenServicioTrasladoProps = {
  form: any;
  cliente: IVision360Cliente;
};

export default function OrdenServicioTraslado({
  form,
  cliente,
}: OrdenServicioTrasladoProps) {
  const [modalMapa, setModalMapa] = useState(false);
  const [serviceType, setServiceType] = useState<ServiceTypeOrdenServicio>(null);
  const [_existeCobertura, setExisteCobertura] = useState(false);
  const [contrato, setContrato] = useState<Contrato | null>(null)

  const serviceOptions = [
    {
      value: "VISITA",
      label: "Visita",
      icon: <Visibility />,
      description:
        "Orden de servicio para visita técnica en sitio del cliente (domicilio o empresa)",
    },
    /* {
      value: "NO_REQUIERE",
      label: "No Requiere",
      icon: <DoNotDisturb />,
      description: "Sin orden de servicio",
    }, */
  ];

  // form
  const {
    reset,
    control,
    formState: { errors },
  } = form;
  console.log('requiere_visita', form.getValues().requiere_visita);

  // hook and store
  const {
    latLng,
    setLatLng,
    Map: MapComponent,
    napsByCity,
  } = useMapComponent({
    form,
    currentCity: "",
    coordenadasCliente: "",
    enabledFetchNaps: true,
    canUpdateLatLong: true,
    async onChangeMarker(value) {
      if (!value) return;
      handleVerificarCoordenada(value.lat.toString(), value.lng.toString());
    },
  });


  const { data: dataContrato, isLoading: isLoadingContrato } =
    useFetchContratos({
      params: {
        identificacion: cliente.identificacion,
      },
    });

  // verificar si existe cobertura acorde a las coordendas ingresadas

  const handleVerificarCoordenada = useCallback(
    async (lat: string, lng: string) => {
      try {
        const resp = await postPoligono({
          latitud: lat,
          longitud: lng,
        });

        reset({
          ...form.getValues(),
          pais: resp.data.pais_data?.codigo,
          zona: resp.data.codigo,
          ciudad: resp.data.ciudad_data?.codigo,
          provincia: resp.data.provincia_data?.codigo,
          provincia_nombre: resp.data.provincia_data?.nombre,
          ciudad_nombre: resp.data.ciudad_data?.nombre,
          zona_nombre: resp.data.nombre,

          // id's
          pais_id: resp.data.pais_data?.id_pais,
          zona_id: resp.data.id_zona,
          ciudad_id: resp.data.ciudad_data?.id_ciudad,
          provincia_id: resp.data.provincia_data?.id_provincia,
        });

        setExisteCobertura(true);
      } catch (error) {
        setExisteCobertura(false);
        reset({
          ...form.getValues(),
          pais: "",
          zona: "",
          ciudad: "",
          provincia: "",
          zona_nombre: "",
          provincia_nombre: "",
          ciudad_nombre: "",
        });
      }
    },
    [],
  );

  // cambio del servicio

  const handleServiceChange = (
    _: React.MouseEvent<HTMLElement>,
    newType: ServiceTypeOrdenServicio,
  ) => {
    form.setValue("tipo_orden_servicio", newType);

    if (newType !== null) {
      setServiceType(newType);
    }
  };




  useLoaders(
    isLoadingContrato
  )

  return (
    <>
      <Typography
        variant="subtitle2"
        color="primary"
        fontWeight={600}
        sx={{ mb: 1 }}
      >
        Orden de Servicio
      </Typography>

      <Box sx={{ p: 3 }}>
        <Divider sx={{ my: 2 }} />

        <CustomAutocomplete<Contrato>
          size={gridSize}
          label="Ubicación del servicio"
          name="contrato"
          options={dataContrato?.data.data || []}
          valueKey="direccion"
          actualValueKey="codigo"
          defaultValue={""}
          isLoadingData={isLoadingContrato}
          control={control}
          error={errors.contrato}
          helperText={errors.contrato?.message}
          onChangeRawValue={(data) => {
            if (!data) {
              toast.info("Seleccionar contrato");
              return;
            }

            reset({
              ...form.getValues(),
              contrato_id: data.id_contrato,
            });

            setContrato(data);
          }}
        />

        <Divider sx={{ my: 2 }} />

        {!contrato && (
          <Grid item {...gridSizeMdLg12}>
            <Alert variant="outlined" severity="info">
              Seleccionar la ubicación del servicio
            </Alert>
          </Grid>
        )}

        {contrato && (
          <>
            <ToggleButtonGroup
              value={serviceType}
              exclusive
              onChange={handleServiceChange}
              fullWidth
              sx={{
                gap: 1.5,
                "& .MuiToggleButtonGroup-grouped": {
                  border: "2px solid",
                  borderColor: "divider",
                },
                "& .MuiToggleButton-root.Mui-selected": {
                  bgcolor: alpha(warmTheme.palette.primary.main, 0.1),
                  borderColor: "primary.main",
                  color: "primary.dark",
                  "&:hover": {
                    bgcolor: alpha(warmTheme.palette.primary.main, 0.15),
                  },
                },
                "& .MuiToggleButton-root:hover": {
                  bgcolor: alpha(warmTheme.palette.secondary.main, 0.1),
                },
              }}
            >
              {serviceOptions.map((option) => (
                <ToggleButton
                  key={option.value}
                  value={option.value}
                  sx={{
                    flexDirection: "column",
                    py: 2,
                    px: 1.5,
                    gap: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      color:
                        serviceType === option.value
                          ? "primary.main"
                          : "text.secondary",
                      transition: "color 0.2s",
                    }}
                  >
                    {option.icon}
                  </Box>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ lineHeight: 1.2 }}
                  >
                    {option.label}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "0.7rem" }}
                  >
                    {option.description}
                  </Typography>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            {serviceType === "VISITA" && (
              <Fade in={serviceType === "VISITA"}>
                <Box sx={{ mt: 2 }}>
                  <VisitaForm contrato={contrato} form={form} />
                </Box>
              </Fade>
            )}

            {serviceType === "NO_REQUIERE" && (
              <Fade in={serviceType === "NO_REQUIERE"}>
                <Box sx={{ mt: 3 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      bgcolor: alpha("#9E9E9E", 0.1),
                      borderRadius: 2,
                      textAlign: "center",
                    }}
                  >
                    <DoNotDisturb
                      sx={{ fontSize: 48, color: "text.secondary", mb: 1 }}
                    />
                    <Typography variant="h6" fontWeight={600} color="text.secondary">
                      Sin Requerimiento
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      No se requiere ninguna acción adicional para este cliente.
                    </Typography>
                  </Paper>
                </Box>
              </Fade>
            )}
          </>
        )}
      </Box>

      {modalMapa && (
        <ScrollableDialogProps
          open={modalMapa}
          title={"Mapa"}
          onCloseIcon={() => setModalMapa(false)}
          contentNode={
            <MapComponent
              key={"mapa-agenda"}
              coordenadas={latLng}
              napsByCity={napsByCity || []}
              setLatLng={setLatLng}
              canDragMarker
            />
          }
        />
      )}
    </>
  );
}
